import React from 'react';
import vdlPrefix from '../util/vdlPrefix';
import classNames from 'classnames';
import { handleBlur, handleFocus, IFocusMixinProps, TimeoutManager } from '@synerg/vdl-react-components';
import { resolveAriaProperty } from '../util/resolveAriaProperty';
import { MDFNumberInput } from './MDFNumberInput';
import { FormatHelper, generateId } from '@adp-wfn/mdf-core';

export interface INumberState {
  focused?: boolean;
  autoFormat?: boolean;
  value: number;
}

export interface INumberProps {
  adaptive?: boolean;
  // Accessibility: links component to the corresponding popover message.
  ariaDescribedby?: string;
  'aria-describedby'?: string;
  ariaInvalid?: any;
  'aria-invalid'?: any;
  ariaLabel?: any;
  'aria-label'?: any;
  autoComplete?: string;
  autoFocus?: boolean;
  autoFormat?: any;
  className?: string;
  culture?: string;
  currencyBoxCallback?: (isValid: boolean, validationMessage: string) => void;
  decimal?: number;
  disabled?: boolean;
  enableAutoSelect?: boolean;
  format?: any;
  // Display informational text below the component.
  helpText?: string;
  id?: string;
  immediate?: boolean;
  limitTrailingZeros?: boolean;
  max?: number;
  maxLength?: number;
  maxValidationMessage?: string;
  min?: number;
  minimumFractionDigits?: number;
  minValidationMessage?: string;
  name?: string;
  parse?: (str: string, culture?: string) => number;
  placeholder?: string;
  readOnly?: boolean;
  regExp?: string;
  regExInvalidMessage?: string;
  required?: boolean;
  tabIndex?: number;
  value?: number;
  onBlur?: (event: any) => void;
  onChange?: (value?: number, isValid?: boolean, validationMessage?: string) => void;
  onFocus?: (event: any) => void;
  onFormatValue?: any;
}

export class MDFNumberBox extends React.Component<INumberProps, INumberState> {
  private inputRef: MDFNumberInput;
  private timeoutManager = new TimeoutManager();

  // The ADP React Components have a hidden dependency on this name and that it's not truly private
  // so that handleBlur() and handleFocus() can inspect the value.
  // tslint:disable-next-line:variable-name
  _isMounted = false;
  isValid = true;
  validationMessage = '';
  private isValidating = false;

  static defaultProps: INumberProps = {
    min: -Infinity,
    max: Infinity,
    enableAutoSelect: false
  };

  state: INumberState = {
    focused: false,
    autoFormat: this.props.autoFormat || false,
    value: this.props.value
  };

  static displayName = 'MDFNumberBox';

  validate = (strVal: string, executeCallBacksOnComponentUpdate = true) => {
    // get the decimalSeperator for the locale
    const delimChar = MDFNumberInput.getlocaleChar(this.props);

    // When decimals other "." is entered replace the decimal character before parsing the value.
    strVal = strVal?.replace(delimChar, '.');

    if (isNaN(parseFloat(strVal))) {
      strVal = '';
    }

    // Parsing string to number before sending it to onChange.
    const numValue = parseFloat(strVal);
    // Check for validation
    this.numberIsValid(numValue);

    // Update the onChange of ValidationField with isValid and validationMessage
    // Onchange that sends the strVal as number to the application.
    // when input value is empty update the value to null
    if (this.props.onChange) {
      const numStrVal = strVal ? Number(strVal) : null;

      // Store current validation state before any changes
      const previousIsValid = this.isValid;
      const previousValidationMessage = this.validationMessage;

      // Check if the input value is empty and not required and make it valid.
      // !numStrVal will return false when the value is 0. Add a check to see if the numStriVal is not 0
      // Handle empty field validation based on required property
      if ((numStrVal !== 0 && !numStrVal)) {
        if (!this.props.required) {
          this.isValid = true;
          this.validationMessage = null;
        }
        else {
          this.isValid = false;
          this.validationMessage = ` ${FormatHelper.formatMessage('@@mdfRequiredField')}`;
        }
      }

      // Only update state if value actually changed to prevent unnecessary re-renders
      if (this.state.value !== numStrVal) {
        this.setState({
          value: numStrVal
        });
      }

      // Segregating callback events execution from validate method, validate method gets called from multiple places in the MDFNumberBox component.
      // Modified validate method signature to accept 2 parameters, second parameter is a optional, from componentDidUpdate method when we call the validate method we do not need to execute callback events, we just need to update component internal state.
      if (executeCallBacksOnComponentUpdate) {
        // Prevent double validation by checking if we're already in a validation cycle
        // and avoid sending null values during component updates unless explicitly required.
        const validationStateChanged = previousIsValid !== this.isValid || previousValidationMessage !== this.validationMessage;
        const shouldSkipCallback = !this.props.required && numStrVal === null && this.isValidating;

        if (!shouldSkipCallback && (this.state.value !== numStrVal || validationStateChanged) && !this.isValidating) {
          this.isValidating = true;
          this.props.onChange(numStrVal, this.isValid, this.validationMessage);

          // Reset flag to allow the current validation cycle to complete.
          void Promise.resolve().then(() => {
            this.isValidating = false;
          });
        }

        this.updateCurrencyBox();
      }
    }
  };

  // wrapped a div to call the onBlur which was causing issue in currencybox as well
  handleNumberBoxBlur = (event) => {
    const focusMixinProps: IFocusMixinProps = {
      props: this.props,
      setTimeout: this.setTimeout,
      focused: this.state.focused,
      setFocused: (focused) => this.setState({ focused }),
      isMounted: this._isMounted
    };

    handleBlur(focusMixinProps, event);
    this.validate(event.target.value);
  };

  updateCurrencyBox = () => {
    // update MDFCurrencyBox isValid and validationMessage values
    if (this.props.currencyBoxCallback) {
      this.props.currencyBoxCallback(this.isValid, this.validationMessage);
    }
  };

  handleNumberBoxFocus = (event) => {
    const focusMixinProps: IFocusMixinProps = {
      props: this.props,
      setTimeout: this.setTimeout,
      focused: this.state.focused,
      setFocused: (focused) => this.setState({ focused }),
      isMounted: this._isMounted
    };

    handleFocus(focusMixinProps, event, undefined,
      (/* focused */) => {
        this.focus();
      }
    );

    if (this.props.enableAutoSelect) {
      setTimeout(() => {
        event.target.select();
      }, 3);
    }
  };

  handleNumberBoxClick = (e) => {
    e.stopPropagation();
    e.nativeEvent.stopImmediatePropagation();
  };

  isExisty(value) {
    return ((value !== null) && (value !== undefined) && (value !== ''));
  }

  componentDidMount() {
    // When the page loads validate the field.
    // Check for validation and update the isValid and validationMessage on the page load.
    // onload validation only when the value exists.
    if (this.isExisty(this.state.value)) {
      this.numberIsValid(this.state.value);
    }

    this._isMounted = true;
  }

  static getDerivedStateFromProps(nextProps, prevState) {
    // Ignore undefined (uncontrolled usage) so user-typed value is preserved.
    if (nextProps.value !== undefined && nextProps.value !== prevState.value) {
      return { value: nextProps.value };
    }

    return null;
  }

  // Validating the number box when there is a change in min, max and value props dynamically.
  componentDidUpdate(prevProps) {
    if (prevProps.min !== this.props.min || prevProps.max !== this.props.max || prevProps.value !== this.props.value) {
      this.validate(this.isExisty(this.props.value) ? this.props.value.toString() : '', false);
    }
  }

  componentWillUnmount() {
    this._isMounted = false;
    this.timeoutManager.clearAllTimeouts();
  }

  // This method is used by the ADP React library.
  setTimeout = (key, callback, duration) => {
    if (this._isMounted) {
      return this.timeoutManager.setTimeout(key, callback, duration);
    }
  };

  focus = () => {
    if (this.inputRef) {
      this.inputRef.getInput().focus();
    }

    this.setState({ focused: true });
  };

  change = (val) => {
    this.validate(val?.toString());
  };

  handleKeyPress = (e) => {
    if (e.charCode === 13) {
      this.validate(e.target.value);
    }
  };

  isValidComponent = (_value) => (
    {
      value: this.state.value,
      isValid: this.isValid,
      validationMessage: this.validationMessage
    }
  );

  isInRange = (value, min, max) => {
    const outOfRangeError = ` ${FormatHelper.formatMessage('@@mdfOutOfRange')}`;

    // Checking with null since value and min can be 0
    if (this.isNumber(value) && this.isNumber(min) && this.isNumber(max)) {
      this.validationMessage = (value < min) ? (this.props.minValidationMessage || outOfRangeError) :
        (value > max) ? (this.props.maxValidationMessage || outOfRangeError) : '';
      return ((value >= min) && (value <= max));
    }

    return false;
  };

  numberIsValid = (value: number) => {
    const max = this.isNumber(this.props.max) ? this.props.max : Infinity;
    const min = this.isNumber(this.props.min) ? this.props.min : -Infinity;

    // Set isValid to false when the number entered is not in min/max range.
    if (!isNaN(value)) {
      this.isValid = this.isInRange(value, min, max);

      if (this.isValid && this.props.regExp && !(new RegExp(this.props.regExp).test(value.toString()))) {
        this.isValid = false;
        this.validationMessage = this.props.regExInvalidMessage || ` ${FormatHelper.formatMessage('@@mdfFailedRegEx')}`;
      }
    }

    this.updateCurrencyBox();

    return this.isValid;
  };

  isNumber = (value) => !((value === undefined) || (value === null) || (value === ''));

  render() {
    const { id, className, required } = this.props;
    const ariaInvalid = resolveAriaProperty('MDFNumberBox', 'aria-invalid', 'ariaInvalid', this.props);
    const ariaLabel = resolveAriaProperty('MDFNumberBox', 'aria-label', 'ariaLabel', this.props);
    const val = this.state.value;
    let negativeNumberFlag = false;
    let ariaLabelValue;
    const numberClasses = {
      [vdlPrefix('textbox')]: true,
      [vdlPrefix('textbox--disabled')]: this.props.disabled,
      [vdlPrefix('textbox--readonly')]: this.props.readOnly,
      [vdlPrefix('textbox--required')]: this.props.required,
      [vdlPrefix('validation-error')]: !this.props.disabled && !this.isValid
    };

    let nodeId = id;

    if (!nodeId) {
      nodeId = generateId('mdfNumberBox');
    }

    // Accessibility: if the number is negative, include aria-label to ensure an accurate screenreader announcement.
    if (val && val < 0) {
      negativeNumberFlag = true;
      ariaLabelValue = val * -1;
    }
    const ariaDescribedby = resolveAriaProperty('MDFNumberBox', 'aria-describedby', 'ariaDescribedby', this.props);

    return (
      <div>
        <MDFNumberInput
          ref={(c) => this.inputRef = c}
          tabIndex={this.props.tabIndex}
          className={classNames(className, numberClasses)}
          placeholder={this.props.placeholder}
          maxLength={this.props.maxLength}
          adaptive={this.props.adaptive}
          value={val}
          formatValue={this.props.onFormatValue}
          decimal={this.props.decimal}
          minimumFractionDigits={this.props.minimumFractionDigits}
          culture={this.props.culture}
          autoFocus={this.props.autoFocus}
          autoComplete={this.props.autoComplete}
          editing={this.state.focused}
          format={this.props.format}
          autoFormat={(this.props.autoFormat !== false) ? true : this.props.autoFormat}
          parse={this.props.parse}
          name={this.props.name}
          min={this.props.min}
          aria-invalid={ariaInvalid}
          aria-disabled={this.props.disabled}
          aria-readonly={this.props.readOnly}
          aria-label={negativeNumberFlag ? FormatHelper.formatMessage('@@negative') + ' ' + ariaLabelValue : ariaLabel || ''}
          aria-describedby={ariaDescribedby}
          required={required}
          disabled={this.props.disabled}
          id={nodeId}
          readOnly={this.props.readOnly}
          onChange={this.change}
          immediate={this.props.immediate}
          onBlur={this.handleNumberBoxBlur}
          onClick={this.handleNumberBoxClick}
          onFocus={this.handleNumberBoxFocus}
          onKeyPress={this.handleKeyPress}
        />
        {this.props.helpText && <div className="text-md">{this.props.helpText}</div>}
      </div>
    );
  }
}
